import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/core/api/apiTypes";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { appointmentManagementApiService } from "../services/appointmentManagementApiService";
import {
  fromAppointmentManagementRequestToDto,
  fromAppointmentManagementResultDto,
  type AppointmentManagementRequest,
  type AppointmentManagementResult,
} from "../types/appointmentManagementTypes";
import type { Appointment } from "../types/appointmentTypes";
import { appointmentKeys } from "./appointmentQueries";

function safeManagementError(error: unknown): Error {
  if (error instanceof ApiError) {
    const messages: Record<number, string> = {
      400: "This request is invalid. Refresh the appointment before continuing.",
      401: "Sign in again before managing this link.",
      403: "Only the business owner can manage this link.",
      404: "Appointment management is unavailable for this appointment.",
      409: "The appointment changed or is busy. Refresh and review it before trying again.",
      503: "Link revocation is temporarily unavailable. Refresh before trying again.",
    };
    return new ApiError(messages[error.Status] ?? "The result could not be confirmed. Refresh the appointment before trying to revoke the link again.", error.Status);
  }
  return new Error("The result could not be confirmed. Refresh the appointment before trying to revoke the link again.");
}

export function useRevokeAppointmentLink(appointmentId: string) {
  const queryClient = useQueryClient();
  const { business, businessId } = useCurrentBusiness();
  const { user, getAuthGeneration } = useAuth();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  return useMutation<AppointmentManagementResult, Error, AppointmentManagementRequest>({
    retry: false,
    gcTime: 0,
    mutationFn: async (request) => {
      if (!user || !businessId || business?.ownerId !== user.id) {
        throw new Error("Only the selected business owner can manage appointment links.");
      }
      const generation = getAuthGeneration();
      const active = () => mounted.current && generation === getAuthGeneration();
      const detailKey = appointmentKeys.detail(appointmentId, businessId, user.id);
      const listKey = appointmentKeys.list(businessId, user.id);
      const refresh = () => Promise.all([
        queryClient.invalidateQueries({ queryKey: detailKey }),
        queryClient.invalidateQueries({ queryKey: listKey }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.events(appointmentId, businessId, user.id) }),
      ]);
      const dto = fromAppointmentManagementRequestToDto(request);
      let result: AppointmentManagementResult;
      try {
        result = fromAppointmentManagementResultDto(await appointmentManagementApiService.revoke(businessId, appointmentId, dto));
      } catch (error) {
        if (active()) await refresh();
        throw safeManagementError(error);
      }
      if (!active()) return result;
      await queryClient.cancelQueries({ queryKey: detailKey });
      await queryClient.cancelQueries({ queryKey: listKey });
      if (!active()) return result;
      queryClient.setQueryData<Appointment>(detailKey, (previous) =>
        previous && previous.revision <= result.revision ? { ...previous, revision: result.revision } : previous);
      queryClient.setQueryData<Appointment[]>(listKey, (previous) => previous?.map((appointment) =>
        appointment.id === appointmentId && appointment.revision <= result.revision
          ? { ...appointment, revision: result.revision } : appointment));
      await refresh();
      return result;
    },
  });
}
